// Helper to capitalize first letter (for languages like French where months/days are lowercase)
const capitalizeFirst = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

export function formatDate(timestamp: number, locale: string = 'en'): string {
  const date = new Date(timestamp * 1000);
  const formatted = date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return capitalizeFirst(formatted);
}

export function calculateSessionsSinceLastYear(sessions?: any[]): number {
  if (!sessions || !Array.isArray(sessions)) {
    return 0;
  }

  const lastYearStart = new Date(new Date().getFullYear() - 1, 0, 1);

  return sessions.filter((session) => {
    if (session.status !== 'COMPLETED') {
      return false;
    }

    const sessionDate = new Date(session.startDate);

    // Derive the latest end time from timeSlots
    const endTimeMinutes =
      session.timeSlots && session.timeSlots.length > 0
        ? Math.max(...session.timeSlots.map((ts: any) => ts.endTime))
        : 0;

    const endDateTime = new Date(sessionDate);
    endDateTime.setHours(Math.floor(endTimeMinutes / 60), endTimeMinutes % 60, 0, 0);

    return endDateTime >= lastYearStart;
  }).length;
}

export interface DateItem {
  date: Date;
  dayName: string;
  dayNumber: number;
}

export function generateAllDatesFromToday(targetMonth: Date, locale: string = 'en'): DateItem[] {
  const dates: DateItem[] = [];
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetYear = targetMonth.getFullYear();
  const targetMonthIndex = targetMonth.getMonth();

  const daysInMonth = new Date(targetYear, targetMonthIndex + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const candidateDate = new Date(targetYear, targetMonthIndex, day);

    if (candidateDate >= today) {
      const dayName = candidateDate.toLocaleDateString(locale, { weekday: 'short' });
      dates.push({
        date: candidateDate,
        dayName: capitalizeFirst(dayName),
        dayNumber: candidateDate.getDate()
      });
    }
  }

  return dates;
}

export function formatDateAsLocalString(date: Date): string {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
}

export function parseUtcDateString(utcDateString: string): Date {
  const parts = utcDateString.split('-');
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
}

export function getMonthYear(date: Date, locale: string = 'en'): string {
  const formatted = date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  return capitalizeFirst(formatted);
}

export function formatDateToLocaleString(
  date: string,
  type: 'short' | 'complete' | 'digits',
  locale: string = 'en'
): string {
  const unformattedDate = parseUtcDateString(date);
  let options: Intl.DateTimeFormatOptions;

  if (type === 'short') {
    options = { weekday: 'short', month: 'short', day: '2-digit' };
    const formatted = unformattedDate.toLocaleDateString(locale, options);
    return capitalizeFirst(formatted);
  } else if (type === 'complete') {
    const weekday = unformattedDate.toLocaleDateString(locale, { weekday: 'long' });
    const day = unformattedDate.toLocaleDateString(locale, { day: '2-digit' });
    const month = unformattedDate.toLocaleDateString(locale, { month: 'long' });
    const year = unformattedDate.toLocaleDateString(locale, { year: 'numeric' });

    return `${capitalizeFirst(weekday)}, ${day} ${capitalizeFirst(month)} ${year}`;
  } else if (type === 'digits') {
    options = { month: '2-digit', day: '2-digit', year: '2-digit' };
    return unformattedDate.toLocaleDateString(locale, options);
  } else {
    throw new Error('Invalid format type');
  }
}
